import { App as CapacitorApp } from '@capacitor/app'
import { Keyboard } from '@capacitor/keyboard'
import { SplashScreen } from '@capacitor/splash-screen'
import { StatusBar } from '@capacitor/status-bar'
import { VueQueryPlugin } from '@tanstack/vue-query'
import { createPinia } from 'pinia'
import {
  ActionBar,
  ActionSheet,
  Button,
  Cell,
  CellGroup,
  Checkbox,
  CheckboxGroup,
  Collapse,
  CollapseItem,
  ConfigProvider,
  Dialog,
  DropdownItem,
  DropdownMenu,
  Empty,
  Field,
  Form,
  Icon,
  ImagePreview,
  Loading,
  NavBar,
  NoticeBar,
  Picker,
  Popup,
  Progress,
  PullRefresh,
  Search,
  Skeleton,
  Stepper,
  SwipeCell,
  Switch,
  Tab,
  Tabbar,
  TabbarItem,
  Tabs,
  Tag,
  Toast,
  Image as VanImage,
} from 'vant'
import { createApp } from 'vue'
import App from './App.vue'
import { configureFeedback } from './platform/feedback'
import { configureNavigation } from './platform/navigation'
import { initializeOta } from './platform/ota'
import { initializeStorage } from './platform/storage'
import { router } from './router'
import 'vant/lib/index.css'
import './styles/main.scss'

async function bootstrap() {
  await initializeStorage()
  const app = createApp(App)
  const pinia = createPinia()

  app
    .use(pinia)
    .use(router)
    .use(VueQueryPlugin)
    .use(ConfigProvider)
    .use(Button)
    .use(Cell)
    .use(CellGroup)
    .use(Field)
    .use(Form)
    .use(Icon)
    .use(VanImage)
    .use(ImagePreview)
    .use(Loading)
    .use(NavBar)
    .use(NoticeBar)
    .use(Popup)
    .use(Progress)
    .use(ActionSheet)
    .use(Dialog)
    .use(Toast)
    .use(Tabbar)
    .use(TabbarItem)
    .use(Tabs)
    .use(Tab)
    .use(Tag)
    .use(Switch)
    .use(Checkbox)
    .use(CheckboxGroup)
    .use(Collapse)
    .use(CollapseItem)
    .use(DropdownMenu)
    .use(DropdownItem)
    .use(Empty)
    .use(Picker)
    .use(PullRefresh)
    .use(Search)
    .use(Skeleton)
    .use(Stepper)
    .use(SwipeCell)
    .use(ActionBar)

  configureNavigation(router)
  configureFeedback()

  router.isReady().then(async () => {
    app.mount('#app')
    await initializeNativeShell()
    void initializeOta()
  })
}

async function initializeNativeShell() {
  try {
    await StatusBar.setOverlaysWebView({ overlay: true })
  }
  catch {
    // Browser preview does not expose native plugins.
  }

  try {
    await Keyboard.setAccessoryBarVisible({ isVisible: false })
  }
  catch {
    // Keyboard plugin is only available in native shells.
  }

  CapacitorApp.addListener('appUrlOpen', ({ url }) => {
    const parsed = new URL(url)
    const target = parsed.searchParams.get('redirect') || parsed.pathname
    if (target.startsWith('/')) {
      void router.replace(target)
    }
  })

  // Android 物理返回：优先关弹层/后退路由，避免直接退出 App
  CapacitorApp.addListener('backButton', ({ canGoBack }) => {
    const overlay = document.querySelector(
      '.van-popup.van-popup--show, .van-action-sheet.van-popup--show, .van-dialog.van-popup--show, .van-overlay',
    )
    if (overlay instanceof HTMLElement) {
      overlay.dispatchEvent(new MouseEvent('click', { bubbles: true }))
      return
    }
    if (canGoBack || window.history.length > 1) {
      router.back()
      return
    }
    void CapacitorApp.exitApp()
  })

  await SplashScreen.hide().catch(() => undefined)
}

void bootstrap()
